import { Image, StyleSheet, Text as RNText, View } from 'react-native';

import { Overline } from '@/components/Overline';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import type { GiftProduct } from '@/types/gifting';

/**
 * Read-only wall of what's on offer, for the marketing landing. Deliberately
 * not the claim grid: nothing here is tappable, because a visitor seeing this
 * may not have an account yet. `GiftProductCard` is the interactive one.
 */
export function GiftPreviewGrid({ products }: { products: GiftProduct[] }) {
  return (
    <View style={styles.preview}>
      <Overline style={styles.previewLabel}>A few of the brands dropping gifts</Overline>
      <View style={styles.previewGrid}>
        {products.map((product) => (
          <View key={product.id} style={styles.previewCard}>
            {product.image_url ? (
              <Image
                source={{ uri: product.image_url }}
                style={styles.previewImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.previewImageFallback}>
                <RNText style={styles.previewFallbackText}>{product.name.slice(0, 1)}</RNText>
              </View>
            )}
            {/* The section label promises "brands dropping gifts" — deliver
                the brand, with the product beneath it. */}
            <RNText style={styles.previewName} numberOfLines={1}>
              {product.brand_name}
            </RNText>
            <RNText style={styles.previewProduct} numberOfLines={1}>
              {product.name}
            </RNText>
            {product.sold_out && <RNText style={styles.previewSoldOut}>Sold out</RNText>}
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  preview: {
    marginTop: spacing.xl,
  },
  previewLabel: {
    marginBottom: spacing.md,
  },
  previewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  previewCard: {
    width: '30%',
    minWidth: 96,
    backgroundColor: colors.surface,
    borderRadius: radii.base,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
  },
  previewImage: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: radii.sm,
    backgroundColor: colors.background,
  },
  previewImageFallback: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  previewFallbackText: {
    fontFamily: fonts.display,
    fontSize: fontSizes['3xl'],
    color: colors.deepGreen,
  },
  previewName: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.xs,
    color: colors.text,
    marginTop: spacing.sm,
  },
  previewProduct: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    marginTop: 2,
  },
  previewSoldOut: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xxs,
    color: colors.textMuted,
    marginTop: 2,
  },
});
